import { SearchBlock } from '@design-system/search-block';
import { Tooltip } from '@design-system/tooltip';
import classNames from 'classnames';
import React, { FC, useRef } from 'react';

import {
  cartIconClassName,
  moreClassName,
  profileClassName,
} from '../../constants/constants';
import { useBrowserScreen } from '../../hooks/useBrowserScreen';
import { useJS } from '../../hooks/useJS';
import { useNavigationResize } from '../../hooks/useNavigationResize';
import { shouldBeRendered } from '../../utils/shouldBeRendered';
import { CartIcon } from '../cart-icon';
import { LanguageSwitch } from '../language-switch';
import { Logo } from '../logo';
import { NavigationBasicItem, NavigationItem } from '../navigation-item';
import { Profile } from '../profile';
import { NavigationDataItem, NavigationProps } from './models';

export const Navigation: FC<NavigationProps> = ({
  navigationName,
  navigationData,
  headerSubtitle,
  linkNavElement,
  onClickNavItem,
  className,
  closeIcon,
  openIcon,
  activeUrlPath,
  logo,
  moreBtn,
  handleMobileNavigationItemClick,
  languageSwitch,
  search,
  cartIcon,
  profile,
  maxVisibleNavItems = 0,
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const { isMobileView } = useBrowserScreen();
  const [isJS] = useJS();
  // const { visibleItems, hiddenItems, isReady } = useNavigationResize({
  //   navigationData,
  //   listRef,
  //   navRef,
  //   maxVisibleNavItems,
  // });

  const visibleItems = navigationData;
  const hiddenItems: NavigationDataItem[] = [];
  const isReady = true;

  return (
    <nav
      ref={navRef}
      className={classNames('cds-global-header__navigation', className)}
      aria-label={navigationName}
    >
      <ul
        ref={listRef}
        style={{ visibility: isJS && !isReady ? 'hidden' : 'visible' }}
        className="cds-global-header__navigation-list"
      >
        {logo && (
          <li className="cds-global-header__navigation-logo">
            <Logo
              className="cds-global-header__desktop-logo"
              image={logo.imageLarge}
              link={logo.link}
            />
            {headerSubtitle && (
              <span className="cds-global-header__navigation-subtitle">
                {headerSubtitle}
              </span>
            )}
          </li>
        )}
        {visibleItems.map((item) => (
          <NavigationItem
            key={item.label}
            linkNavElement={linkNavElement}
            activeUrlPath={activeUrlPath}
            isMobileView={isMobileView}
            openIcon={openIcon}
            closeIcon={closeIcon}
            expandTitle={moreBtn.title}
            isJS={isJS}
            onClickNavItem={onClickNavItem}
            handleMobileNavigationItemClick={handleMobileNavigationItemClick}
            {...item}
          />
        ))}
        {/* More button below */}
        {shouldBeRendered(
          moreBtn,
          [isJS, !isMobileView, hiddenItems?.length > 0],
          'and'
        ) ? (
          <NavigationBasicItem
            className={classNames(moreClassName, {
              [`${moreClassName}--hidden`]: !navigationData.length,
            })}
            linkNavElement={linkNavElement}
            activeUrlPath={activeUrlPath}
            isMobileView={isMobileView}
            openIcon={openIcon}
            closeIcon={closeIcon}
            expandTitle={moreBtn.title}
            subItems={hiddenItems}
            ariaLabel={moreBtn?.ariaLabel}
            handleMobileNavigationItemClick={handleMobileNavigationItemClick}
            isJS={isJS}
            label={moreBtn.title}
            onClickNavItem={onClickNavItem}
            href=""
          />
        ) : null}
        {languageSwitch ? (
          <LanguageSwitch
            linkNavElement={linkNavElement}
            openIcon={openIcon}
            closeIcon={closeIcon}
            isMobileView={isMobileView}
            {...languageSwitch}
          />
        ) : null}
        {search && !isMobileView ? (
          <li
            className="cds-global-header__navigation-search
                        cds-global-header__navigation-icon"
          >
            <SearchBlock
              {...search}
              handleBurgerMenu={handleMobileNavigationItemClick}
            />
          </li>
        ) : null}
        {cartIcon && !isMobileView ? (
          <li
            className={classNames(
              cartIconClassName,
              'cds-global-header__navigation-icon'
            )}
          >
            <CartIcon {...cartIcon} />
          </li>
        ) : null}
        {profile ? (
          <li
            className={classNames(profileClassName, {
              'cds-global-header__navigation-icon': !isMobileView,
            })}
          >
            {!isMobileView ? (
              <Tooltip
                tooltipWindowClassName="cds-global-header-profile-window"
                portalQuerySelector=".cds-tooltip"
                label={profile.headline}
                isTooltipContentInteractive
                icon="profile"
                content={<Profile {...profile} />}
              />
            ) : (
              <Profile {...profile} isMobileView={isMobileView} />
            )}
          </li>
        ) : null}
      </ul>
    </nav>
  );
};
